import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type {
  FilterGroups,
  FilterValue,
} from 'src/components/app/context/FilterContext';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import { ORG_UNIT_FILTER_NAME } from 'src/components/common/OrgTree/types';
import type {
  DateRangeApiString,
  DateRangeAsStrings,
} from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';
import { dateRangeFilterToApiString } from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';

import { FILTER_KEYS } from './constants';

export type UseFollowUpFiltersReturn = {
  orgUnit: string;
  dateRange?: DateRangeApiString;
  comparisonDateRange?: DateRangeApiString;
  followUpFilters: Record<string, string>;
  demographicsFilters: Record<string, string>;
};

type UseFollowUpFilters = (isComparison?: boolean) => UseFollowUpFiltersReturn;

const followUpFilterKeys = ['npsGroup', 'status', 'followUpOwner'];
const specialFilterKeys = [
  ORG_UNIT_FILTER_NAME,
  FILTER_KEYS.DATE_RANGE,
  FILTER_KEYS.DATE_RANGE_COMPARISON,
];

export const mapDemographicsFilters = (
  filterGroups: FilterGroups,
  t: TFunction<'followUp'>,
  valuesOrLabels: 'values' | 'labels' = 'values',
) =>
  Object.fromEntries(
    // Get only demographics filters and replace commas with pipes
    // because commas are used as dividers in the search params
    // The pipes get rereplaced with commas in the backend
    Object.entries(filterGroups)
      .filter(
        (entry) =>
          ![
            ...specialFilterKeys,
            ...followUpFilterKeys.map((filterKey) =>
              t(`tableHeader.${filterKey}`),
            ),
          ].includes(entry[0]),
      )
      .map(([key, value]) => [
        key.toLowerCase(),
        value
          .map(({ label, filterExpr }) => {
            if (valuesOrLabels === 'labels') return label;
            return Object.values(filterExpr).map((expression) =>
              expression.replace(/,/g, '|'),
            );
          })
          .toString(),
      ]),
  );

export const mapFollowUpFilters = (
  filterGroups: FilterGroups,
  t: TFunction<'followUp'>,
  valuesOrLabels: 'values' | 'labels' = 'values',
) =>
  Object.fromEntries(
    followUpFilterKeys.map((filterKey) => [
      filterKey,
      filterGroups[t(`tableHeader.${filterKey}`)]
        ?.map(({ label, filterExpr }) => {
          if (valuesOrLabels === 'labels') return label;
          return Object.values(filterExpr);
        })
        .toString(),
    ]),
  );

const useFollowUpFilters: UseFollowUpFilters = () => {
  const { filterGroups } = useFilterContext();
  const { t } = useTranslation('followUp');

  const demographicsFilters = useMemo(
    () => mapDemographicsFilters(filterGroups, t),
    [filterGroups, t],
  );
  const followUpFilters = useMemo(
    () => mapFollowUpFilters(filterGroups, t),
    [filterGroups, t],
  );

  const orgUnit = useMemo(
    () =>
      filterGroups[ORG_UNIT_FILTER_NAME]?.map(
        (orgUnit) => orgUnit.filterExpr.orgUnitDbId,
      ).toString(),
    [filterGroups],
  );

  const dateRange = useMemo(
    () =>
      filterGroups[FILTER_KEYS.DATE_RANGE]?.[0]
        ? dateRangeFilterToApiString(
            filterGroups[
              FILTER_KEYS.DATE_RANGE
            ][0] as FilterValue<DateRangeAsStrings>,
          )
        : undefined,
    [filterGroups[FILTER_KEYS.DATE_RANGE]],
  );

  const comparisonDateRange = useMemo(
    () =>
      filterGroups[FILTER_KEYS.DATE_RANGE_COMPARISON]?.[0]
        ? dateRangeFilterToApiString(
            filterGroups[
              FILTER_KEYS.DATE_RANGE_COMPARISON
            ][0] as FilterValue<DateRangeAsStrings>,
          )
        : undefined,
    [filterGroups[FILTER_KEYS.DATE_RANGE_COMPARISON]],
  );

  return useMemo(
    () => ({
      followUpFilters,
      demographicsFilters,
      dateRange,
      orgUnit,
      comparisonDateRange,
    }),
    [
      followUpFilters,
      demographicsFilters,
      dateRange,
      orgUnit,
      comparisonDateRange,
    ],
  );
};
export default useFollowUpFilters;
